import type { AdminAccount } from '@inflip/contracts/admin';
import { useEffect, useState } from 'react';

import { Button, ErrorBanner, Section, inputCls } from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';

/** 내 정보 — 이름 수정 + 비밀번호 변경. 상단 헤더 아바타에서 진입. */
export function MePage() {
  const { session } = useAdminAuth();
  const [me, setMe] = useState<AdminAccount | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState<string | null>(null);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    api
      .me(session.accessToken)
      .then((account) => {
        if (cancelled) return;
        setMe(account);
        setName(account.name);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)));
    return () => {
      cancelled = true;
    };
  }, [session]);

  if (!session) return null;

  async function saveName(e: React.FormEvent) {
    e.preventDefault();
    if (!session || savingName || !name.trim()) return;
    setSavingName(true);
    setNameMsg(null);
    try {
      const updated = await api.updateMe(session.accessToken, { name: name.trim() });
      setMe(updated);
      setNameMsg('저장되었습니다.');
    } catch (err) {
      setNameMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingName(false);
    }
  }

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!session || savingPw) return;
    if (newPw !== newPw2) {
      setPwMsg({ ok: false, text: '새 비밀번호가 서로 다릅니다.' });
      return;
    }
    setSavingPw(true);
    setPwMsg(null);
    try {
      await api.changeMyPassword(session.accessToken, {
        currentPassword: currentPw,
        newPassword: newPw,
      });
      setCurrentPw('');
      setNewPw('');
      setNewPw2('');
      setPwMsg({ ok: true, text: '비밀번호가 변경되었습니다.' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setPwMsg({
        ok: false,
        text: msg.includes('현재 비밀번호')
          ? '현재 비밀번호가 일치하지 않습니다.'
          : msg.includes('10자')
            ? '새 비밀번호는 10자 이상이어야 합니다.'
            : msg,
      });
    } finally {
      setSavingPw(false);
    }
  }

  return (
    <main className="mx-auto w-full max-w-xl px-6 py-8">
      {error && <ErrorBanner message={error} />}

      {me && (
        <>
          <header className="flex items-center gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-brand-soft text-xl font-extrabold text-brand">
              {me.name.slice(0, 1)}
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-2xl font-extrabold tracking-tight">{me.name}</h1>
                <span className="text-sm font-bold text-brand">
                  {me.role === 'SUPER_ADMIN' ? '슈퍼어드민' : '어드민'}
                </span>
              </div>
              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                {me.email} · MFA {me.mfaEnrolled ? '등록됨' : '미등록'}
              </p>
            </div>
          </header>

          <Section title="이름">
            <form onSubmit={saveName} className="flex gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`min-w-0 flex-1 ${inputCls}`}
                maxLength={100}
              />
              <Button
                type="submit"
                disabled={savingName || !name.trim() || name.trim() === me.name}
              >
                저장
              </Button>
            </form>
            {nameMsg && <p className="mt-2 text-xs text-muted-foreground">{nameMsg}</p>}
          </Section>

          <Section title="비밀번호 변경">
            <form onSubmit={savePassword} className="space-y-2">
              <input
                type="password"
                placeholder="현재 비밀번호"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className={`w-full ${inputCls}`}
                autoComplete="current-password"
              />
              <input
                type="password"
                placeholder="새 비밀번호 (10자 이상)"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className={`w-full ${inputCls}`}
                autoComplete="new-password"
              />
              <input
                type="password"
                placeholder="새 비밀번호 확인"
                value={newPw2}
                onChange={(e) => setNewPw2(e.target.value)}
                className={`w-full ${inputCls}`}
                autoComplete="new-password"
              />
              <Button
                type="submit"
                disabled={savingPw || !currentPw || newPw.length < 10 || !newPw2}
              >
                변경
              </Button>
            </form>
            {pwMsg && (
              <p className={`mt-2 text-xs ${pwMsg.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                {pwMsg.text}
              </p>
            )}
          </Section>
        </>
      )}
    </main>
  );
}
